import { FlexCol, FlexRow } from '@/components/ui/flex';
import { Text } from '@/components/ui/text';
import { UITools } from '@ditto/shared';
import { ToolUIPart } from 'ai';
import { Brain, Layers, ScanSearch, FileSearch } from 'lucide-react';
import { FC } from 'react';
import { AIReasoningItem } from '../../AiReasoningBlock';
import { ApplicableStandardsSearchDisplay } from './ToolDisplays/ApplicableStandardsSearchDisplay';
import { DeviceClassificationToolMessage } from './ToolDisplays/DeviceClassification';
import { Fda510kSubmissionDbSearchToolMessage } from './ToolDisplays/Fda510kSubmissionDbSearchToolMessage';
import { FDAAdverseEventsSearchDisplay } from './ToolDisplays/FDAAdverseEventsSearchDisplay';

const toolMap = {
  listKnowledgeBase: {
    loadingText: 'Searching for relevant knowledge base documents...',
    successText: 'Done searching for relevant knowledge base documents',
    errorText: 'Error scanning internal knowledge base',
    icon: ScanSearch,
    isOpenable: false,
  },
  knowledgeSearch: {
    loadingText: 'Scanning knowledge base documents...',
    successText: 'Done scanning knowledge base documents',
    errorText: 'Error scanning internal knowledge base',
    icon: Brain,
    isOpenable: true,
  },
  fdaSearch: {
    loadingText: 'Searching for FDA information...',
    successText: 'FDA search completed',
    errorText: 'Error searching for FDA information',
    icon: ScanSearch,
    isOpenable: false,
  },
  fda510kSubmissionDbSearch: {
    loadingText: 'Analyzing 510(k) premarket notifications database...',
    successText:
      'Analysis completed in 510(k) premarket notifications database',
    errorText: 'Error analyzing 510(k) premarket notifications database',
    icon: Layers,
    isOpenable: true,
  },
  fdaDeviceClassificationDbSearch: {
    loadingText: 'Searching for FDA device classification...',
    successText: 'FDA device classification search completed',
    errorText: 'Error searching for FDA device classification',
    icon: Layers,
    isOpenable: true,
  },
  fdaAdverseEventsSearch: {
    loadingText: 'Searching FDA adverse events database...',
    successText: 'FDA adverse events search completed',
    errorText: 'Error searching FDA adverse events database',
    icon: Layers,
    isOpenable: true,
  },
  applicableStandardsSearch: {
    loadingText:
      'Searching for applicable standards (this may take 2-3 minutes)...',
    successText: 'Standards search completed',
    errorText: 'Error searching for applicable standards',
    icon: FileSearch,
    isOpenable: true,
  },
};

// Compute aggregate status from multiple tool parts
const computeGroupStatus = (
  parts: ToolUIPart<UITools>[]
): 'loading' | 'error' | 'success' => {
  const isAnyLoading = parts.some(
    p => p.state === 'input-available' || p.state === 'input-streaming'
  );
  const isAnyError = parts.some(p => p.state === 'output-error');

  if (isAnyLoading) return 'loading';
  if (isAnyError) return 'error';
  return 'success';
};

// Check if any tool part has summarized output
const hasAnySummarizedOutput = (parts: ToolUIPart<UITools>[]): boolean => {
  return parts.some(
    p =>
      (p.output as { _summarized?: boolean } | undefined)?._summarized === true
  );
};

export const Tool: FC<{
  toolName: string;
  toolParts: ToolUIPart<UITools>[];
  isLast: boolean;
}> = ({ toolName, toolParts, isLast }) => {
  const tool = toolMap[toolName as keyof typeof toolMap];

  // We dont show tools not listed above
  if (!tool) {
    return null;
  }

  const status = computeGroupStatus(toolParts);

  // We dont show tools if all are erroring
  if (status === 'error') {
    return null;
  }

  // Filter out errored parts for display
  const validParts = toolParts.filter(p => p.state !== 'output-error');

  // Build display message with call count if multiple calls
  const callCount = validParts.length;
  const displayMessages = {
    success:
      callCount > 1
        ? `${tool.successText} (${callCount} searches)`
        : tool.successText,
    error: tool.errorText,
    loading:
      callCount > 1
        ? `${tool.loadingText} (${callCount} searches)`
        : tool.loadingText,
  };

  // Determine if accordion should be openable
  const isOpenable = tool.isOpenable && !hasAnySummarizedOutput(validParts);

  return (
    <AIReasoningItem
      messages={displayMessages}
      status={status}
      isOpenable={isOpenable}
      icon={tool.icon}
      isLast={isLast}
    >
      <GroupedToolDetails toolName={toolName} toolParts={validParts} />
    </AIReasoningItem>
  );
};

// Component to render details for multiple tool calls
const GroupedToolDetails: FC<{
  toolName: string;
  toolParts: ToolUIPart<UITools>[];
}> = ({ toolName, toolParts }) => {
  // Only render parts that have both input and output
  const completedParts = toolParts.filter(p => p.input && p.output);

  if (completedParts.length === 0) {
    return null;
  }

  return (
    <FlexCol gap="4" className="w-full">
      {completedParts.map((part, idx) => (
        <FlexCol key={idx} gap="2" className="w-full">
          {completedParts.length > 1 && (
            <Text variant="body-sm" color="secondary" className="font-medium">
              Search {idx + 1} of {completedParts.length}
            </Text>
          )}
          <ToolDetailDisplay toolName={toolName} toolPart={part} />
          {idx < completedParts.length - 1 && (
            <div className="border-b border-neutral-200 my-2" />
          )}
        </FlexCol>
      ))}
    </FlexCol>
  );
};

// Component to render the specific tool display based on tool type
const ToolDetailDisplay: FC<{
  toolName: string;
  toolPart: ToolUIPart<UITools>;
}> = ({ toolName, toolPart }) => {
  if (
    toolName === 'fdaDeviceClassificationDbSearch' &&
    toolPart.input &&
    toolPart.output
  ) {
    return (
      <DeviceClassificationToolMessage
        input={
          toolPart.input as UITools['fdaDeviceClassificationDbSearch']['input']
        }
        output={
          toolPart.output as UITools['fdaDeviceClassificationDbSearch']['output']
        }
      />
    );
  }

  if (
    toolName === 'fda510kSubmissionDbSearch' &&
    toolPart.input &&
    toolPart.output
  ) {
    return (
      <Fda510kSubmissionDbSearchToolMessage
        input={toolPart.input as UITools['fda510kSubmissionDbSearch']['input']}
        output={
          toolPart.output as UITools['fda510kSubmissionDbSearch']['output']
        }
      />
    );
  }

  if (
    toolName === 'fdaAdverseEventsSearch' &&
    toolPart.input &&
    toolPart.output
  ) {
    return (
      <FDAAdverseEventsSearchDisplay
        input={toolPart.input as UITools['fdaAdverseEventsSearch']['input']}
        output={toolPart.output as UITools['fdaAdverseEventsSearch']['output']}
      />
    );
  }

  if (
    toolName === 'applicableStandardsSearch' &&
    toolPart.input &&
    toolPart.output
  ) {
    return (
      <ApplicableStandardsSearchDisplay
        input={toolPart.input as UITools['applicableStandardsSearch']['input']}
        output={
          toolPart.output as UITools['applicableStandardsSearch']['output']
        }
      />
    );
  }

  if (toolName === 'knowledgeSearch' && toolPart.input) {
    return (
      <KnowledgeSearchDisplay
        input={toolPart.input as UITools['knowledgeSearch']['input']}
      />
    );
  }

  return null;
};

// Display component for knowledgeSearch tool
const KnowledgeSearchDisplay: FC<{
  input: UITools['knowledgeSearch']['input'];
}> = ({ input }) => {
  return (
    <FlexRow alignItems="start" gap="2" className="w-full">
      <Text variant="body" className="text-neutral-500">
        Searched for:
      </Text>
      <Text variant="body" className="text-neutral-700 italic">
        "{input.question}"
      </Text>
    </FlexRow>
  );
};
